import {MatchingDetailsLigne} from 'api-types/ffconnect';
import {TFunction} from 'i18next';
import {prixLigneAEteCompare} from '@/backoffice/domains/PdfOrderImport/utils';

/**
 * Import PDF : la phrase de contrôle affichée à droite du statut d'une ligne,
 * à la place du résumé technique « ID / Code / Libellé / Prix ».
 */

// Chaque combinaison a quatre phrases, une par situation de prix : prix exact,
// léger écart, gros écart, pas de prix. Cette fonction choisit laquelle. Le
// rapport, lui, ne connaît que trois états (`ok` / `alerte` / `bloquant`) et
// n'a rien pour dire « pas de prix » : ce quatrième cas se déduit.
const etatDuPrix = (
  matching: MatchingDetailsLigne,
): 'ok' | 'alerte' | 'ko' | 'vide' => {
  const prix = matching.prix_ligne_total;
  if (!prixLigneAEteCompare(prix)) return 'vide';
  if (prix?.etat === 'ok') return 'ok';
  if (prix?.etat === 'alerte') return 'alerte';
  return 'ko';
};

// Les états du rapport qui nomment les groupes de phrases dans les locales.
const NOM_ETAT_CODE = {
  exact: 'code_exact',
  ko: 'code_different',
  vide: 'code_absent',
};

const NOM_ETAT_LIBELLE = {
  exact: 'libelle_exact',
  partiel: 'libelle_proche',
  ko: 'libelle_non_reconnu',
};

// Comment le rapport a identifié l'article : par son code, par son libellé, ou
// pas vraiment. Renvoie le couple des deux états sous la forme
// `code_exact_libelle_proche`, qui désigne l'un des neuf groupes de phrases.
const combinaisonIdentification = (matching: MatchingDetailsLigne): string => {
  // Sans état de code ou de libellé, on lit le rapport au pire : même
  // convention que `etatDeLaLigne`, où l'absence d'état vaut « bloquant ».
  const code = matching.code?.etat ?? 'vide';
  const libelle = matching.libelle?.etat ?? 'ko';
  return `${NOM_ETAT_CODE[code]}_${NOM_ETAT_LIBELLE[libelle]}`;
};

// L'écart de prix, toujours signé. `Intl` applique la typographie de la langue
// affichée, qui n'écrit pas un pourcentage de la même façon partout : « +10,0 % »
// en français et en allemand (virgule, espace insécable), « +10.0% » en anglais.
const formatEcartPourcentage = (ecart: number, langue: string): string => {
  return new Intl.NumberFormat(langue, {
    style: 'percent',
    signDisplay: 'always',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ecart / 100);
};

// La phrase entière. Chaque combinaison porte déjà son verdict (« Ligne OK
// car … » / « Ligne à vérifier … »), aligné sur l'état global que le serveur a
// calculé : le badge et la phrase disent donc toujours la même chose.
export const phraseDeControle = (
  t: TFunction,
  matching: MatchingDetailsLigne,
  langue: string,
): string => {
  const cle = `panier.matching_phrase.${combinaisonIdentification(matching)}.${etatDuPrix(matching)}`;
  return t(cle, {
    ecart: formatEcartPourcentage(
      matching.prix_ligne_total?.ecart_pourcentage ?? 0,
      langue,
    ),
  });
};
